import { NextPage } from 'next';
import React, { FC } from 'react';
import { useIntl } from 'react-intl';
import JsonLd from '../../../components/layouts/JsonLd';
import MetaTags from '../../../components/layouts/MetaTags';
import {
  allCategoriesCache,
  getCategory,
} from '../../../infrastructure/api/backend/explore_api';
import { CategoryEntity } from '../../../interfaces/explore/CategoryEntity';
import { MerchantDetail } from '../../../interfaces/merchants';
import { fetchMerchantDetail } from '../../../infrastructure/api';
import ErrorTemplate from '../../../templates/ErrorTemplate';
import { MerchantSite } from '../../../components/MerchantSite';
import CouponIndexTemplate from '../../../templates/merchants/coupons/IndexTemplate';
import { getCookieCredentialsOnServerSide } from '../../../utils/ssr';
import {
  fetchMerchantCouponsForSSR,
  MerchantVoucher,
} from '../../../infrastructure/api/coupon';
import { usePushMerchantDataLayerEffect } from '../../../hooks/gtm';
import { injectAppContextGetServerSideProps } from '../../../pages/_app';
import { buildSiteUrl } from 'src/utils/request-url';

type Props = {
  merchant?: MerchantDetail | null;
  category?: CategoryEntity;
  coupons?: MerchantVoucher[];
  isUnpublishedSite: boolean;
  isCoubicApp: boolean;
};

const Coupons: FC<Props> = ({ merchant, category, coupons, isCoubicApp }) => {
  const intl = useIntl();
  usePushMerchantDataLayerEffect({ visit_merchant_id: merchant?.id });

  if (merchant && category && coupons) {
    return (
      <>
        <MetaTags
          title={intl.formatMessage(
            { id: 'coupons.index.meta.title' },
            { name: merchant.display_name },
          )}
          description={
            merchant.seo_setting?.description || merchant.description
          }
          keywords={merchant.seo_setting?.keyword || ''}
          thumbnailUrl={merchant.cover_image.url || ''}
          currentPath={buildSiteUrl(`/coupons`)}
          noIndex={merchant.seo_setting?.no_index}
          googleAnalyticsFourTrackingId={
            merchant.available_google_analytics_four_tracking_id
          }
          faviconImageUrl={merchant.favicon_image?.url}
        />
        <JsonLd merchant={merchant} />
        <CouponIndexTemplate
          merchant={merchant}
          isCoubicApp={isCoubicApp}
          coupons={coupons.map((coupon) => {
            return { name: coupon.name, publicId: coupon.public_id };
          })}
        />
      </>
    );
  }
  return <ErrorTemplate statusCode={404} />;
};

const MerchantCoupons: NextPage<Props> = (props) => {
  const { merchant, isUnpublishedSite } = props;

  return (
    <MerchantSite
      isUnPublished={isUnpublishedSite}
      isMerchantInfoExist={!!merchant}
      logoImage={merchant?.logo_image || null}
      merchantPublicId={merchant?.public_id || null}
      displayName={merchant?.display_name || null}
      isCoubicApp={props.isCoubicApp}
    >
      <Coupons {...props} />
    </MerchantSite>
  );
};

export const getServerSideProps = injectAppContextGetServerSideProps<Props>(
  async (ctx) => {
    const { query, isCoubicApp } = ctx;
    const merchantPublicId = String(query.publicId);
    const { cookieSession, jwtToken } = getCookieCredentialsOnServerSide(ctx);

    const merchantDetailResponse = await fetchMerchantDetail({
      session: cookieSession,
      token: jwtToken,
      requestId: '',
      merchantPublicId,
    });
    if (merchantDetailResponse.redirect) {
      return { redirect: merchantDetailResponse.redirect };
    }
    const { merchant, isUnpublishedSite } = merchantDetailResponse;

    if (isUnpublishedSite) {
      return {
        props: {
          merchant: merchant ?? null,
          isUnpublishedSite,
          categories: [],
          coupons: [],
          isCoubicApp,
        },
      };
    }

    const [categories, coupons] = await Promise.all([
      allCategoriesCache(),
      fetchMerchantCouponsForSSR({
        session: cookieSession,
        token: jwtToken,
        requestId: '',
        merchantPublicId,
      }),
    ]);

    const category = merchant
      ? await getCategory(merchant.category_id)
      : undefined;
    return {
      props: {
        merchant,
        isUnpublishedSite,
        categories,
        category,
        coupons,
        isCoubicApp,
      },
    };
  },
);

export default MerchantCoupons;
